/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSearchParams, useNavigate, useParams, Link, useLocation, matchPath } from 'react-router-dom';
import { t } from 'i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';

// Component imports
import { Typography } from 'antd';
import { BotLabSpaceIcon } from '../Icons/BotLabSpaceIcon';
import { LiveChatSpaceIcon } from '../Icons/LiveChatSpaceIcon';
import { OrganizationSpaceIcon } from '../Icons/OrganizationSpaceIcon';
import { ObservatorySpaceIcon } from '../Icons/ObservatorySpaceIcon';
import { LabSwitcherMask } from './LabSwitcherMask';
import { LabSwitcherArrowIcon } from '../Icons/LabSwitcherArrowIcon';

// Μisc imports
import { LABS } from '../../constants/main';
import { IS_LIVECHAT_CONSOLE_ONLY, IS_LIVECHAT_CONVERSATIONS_ENABLED } from '../../constants/env';
import { NAV_BOT, NAV_BOT_LOADING, NAV_CHAT_SESSIONS, NAV_DASHBOARD, NAV_LIVECHAT, NAV_NO_BOT } from '../../constants/navigation';
import { LIVECHAT_SCREEN_TABS } from '../../constants/liveChat';
import { useFetchFirstAvailableBotQuery } from '../../store/bots/botsApi';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import hasLiveChatAccess from '../../utils/misc/hasLiveChatAccess';
import is from '../../utils/is';

// Constant declarations
const baseClassName = 'lab-switcher';

// Reposition the "left" property icon mask to the correct position
const repositionIconMask = () => {
  const iconMask = document.querySelector(`.${baseClassName}__icon-mask`);
  if (iconMask) {
    const activeIcon = document.querySelector(`.${baseClassName}__icon--active`);
    if (activeIcon) {
      const activeIconRect = activeIcon.getBoundingClientRect();
      iconMask.style.left = `${activeIconRect.left - 17}px`;
    }
  }
};

const LabSwitcher = ({ onArrowClick, currentLab, setCurrentLab }) => {
  const { isSidebarOpen } = useSelector((state) => state.global);
  const { currentUser } = useSelector((state) => state.auth);
  const { orgId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { recentBots } = useSelector((state) => state.bots);
  const recentBotsKey = (recentBots || []).map((bot) => bot?.id).filter(Boolean).join(',');

  // We fetch the first available bot to redirect the user to it when they switch to the Designer tab
  const { currentData: firstBot, isFetching: isLoadingFirstBot, refetch: refetchFirstAvailableBot } = useFetchFirstAvailableBotQuery(
    { orgId, recentBotsKey },
    { skip: !orgId, refetchOnMountOrArgChange: true }
  );

  const tenantId = firstBot?.id || firstBot?.tenantId;
  const botPath = useMemo(() => {
    if (isLoadingFirstBot && !firstBot){
      return replaceUrlParams(NAV_BOT_LOADING, { orgId });
    }
    if (tenantId) {
      return replaceUrlParams(NAV_BOT, { tenantId, orgId });
    }
  }, [tenantId, orgId]);
  const LABS_PATHS = {
    ORG: replaceUrlParams(NAV_DASHBOARD, { orgId }),
    BOT: botPath,
    LIVECHAT: `${replaceUrlParams(NAV_LIVECHAT, { orgId })}?${createSearchParams({ tab: LIVECHAT_SCREEN_TABS.NEW }).toString()}`,
    OBSERVATORY: replaceUrlParams(NAV_CHAT_SESSIONS, { orgId })
  };

  useEffect(() => {
    if (matchPath(NAV_BOT, location.pathname)) {
      setCurrentLab(LABS.BOT.key);
      return;
    }

    if (matchPath(NAV_LIVECHAT, location.pathname)) {
      setCurrentLab(LABS.LIVECHAT.key);
      return;
    }

    if (matchPath(NAV_CHAT_SESSIONS, location.pathname)) {
      setCurrentLab(LABS.OBSERVATORY.key);
      return;
    }

    if (matchPath(NAV_DASHBOARD, location.pathname)) {
      setCurrentLab(LABS.ORG.key);
      return;
    }

  }, [location.pathname, setCurrentLab]);

  useEffect(() => {
    // Refetch the first available bot when the current lab changes
    if (orgId) {
      refetchFirstAvailableBot();
    }
  }, [currentLab, orgId, refetchFirstAvailableBot]);

  useEffect(() => {
    // Reposition the mask when the component mounts or when the current lab changes
    repositionIconMask();
  }, [currentLab, isSidebarOpen]);

  const changeLab = (lab) => {
    // Special case: no available bots -> go to "no agents" screen
    if (lab === LABS.BOT.key && !tenantId && !isLoadingFirstBot) {
      navigate(replaceUrlParams(NAV_NO_BOT, { orgId }));
      return;
    }

    // Navigate to the correct path when the lab changes
    const path = LABS_PATHS[lab];
    if (path) {
      navigate(path);
    }
  };

  const renderIcons = () => {
    if ((IS_LIVECHAT_CONSOLE_ONLY && hasLiveChatAccess(currentUser, orgId)) || is.inMicrosoftTeams()) {
      return (
        <Link to={LABS_PATHS.LIVECHAT}>
          <LiveChatSpaceIcon
            className={classNames(`${baseClassName}__icon`, `${baseClassName}__icon--livechat`, {
              [`${baseClassName}__icon--active`]: currentLab === LABS.LIVECHAT.key
            })}
            {...{ color: currentLab === LABS.LIVECHAT.key ? 'transparent' : 'primary' }}
            onClick={() => changeLab(LABS.LIVECHAT.key)}
          />
        </Link>
      );
    } else {
      return (
        <Fragment>
          <Link to={LABS_PATHS.ORG}>
            <OrganizationSpaceIcon
              className={classNames(`${baseClassName}__icon`, `${baseClassName}__icon--org`, {
                [`${baseClassName}__icon--active`]: currentLab === LABS.ORG.key
              })}
              {...{ color: currentLab === LABS.ORG.key ? 'transparent' : 'primary' }}
              onClick={() => changeLab(LABS.ORG.key)}
            />
          </Link>

          <Link to={LABS_PATHS.BOT}>
            <BotLabSpaceIcon
              className={classNames(`${baseClassName}__icon`, `${baseClassName}__icon--bot`, {
                [`${baseClassName}__icon--active`]: currentLab === LABS.BOT.key
              })}
              {...{ color: currentLab === LABS.BOT.key ? 'transparent' : 'primary' }}
              onClick={() => changeLab(LABS.BOT.key)}
              isLoading={isLoadingFirstBot && !firstBot}
            />
          </Link>

          <Link to={LABS_PATHS.OBSERVATORY}>
            <ObservatorySpaceIcon
              className={classNames(`${baseClassName}__icon`, `${baseClassName}__icon--observatory`, {
                [`${baseClassName}__icon--active`]: currentLab === LABS.OBSERVATORY.key
              })}
              {...{ color: currentLab === LABS.OBSERVATORY.key ? 'transparent' : 'primary' }}
              onClick={() => changeLab(LABS.OBSERVATORY.key)}
            />
          </Link>

          {
            (IS_LIVECHAT_CONSOLE_ONLY || IS_LIVECHAT_CONVERSATIONS_ENABLED) && hasLiveChatAccess(currentUser, orgId) ? (
              <Link to={LABS_PATHS.LIVECHAT}>
                <LiveChatSpaceIcon
                  className={classNames(`${baseClassName}__icon`, `${baseClassName}__icon--livechat`, {
                    [`${baseClassName}__icon--active`]: currentLab === LABS.LIVECHAT.key
                  })}
                  {...{ color: currentLab === LABS.LIVECHAT.key ? 'transparent' : 'primary' }}
                  onClick={() => changeLab(LABS.LIVECHAT.key)}
                />
              </Link>
            ) : null
          }
        </Fragment>
      );
    }
  };

  const renderActiveIcon = () => {
    let currentComponent = null;

    switch (currentLab) {
      case LABS.ORG.key:
        currentComponent = (
          <OrganizationSpaceIcon
            className={`${baseClassName}__icon ${baseClassName}__icon--org ${baseClassName}__icon--active`}
            color="transparent"
          />
        );
        break;
      case LABS.BOT.key:
        currentComponent = (
          <BotLabSpaceIcon
            className={`${baseClassName}__icon ${baseClassName}__icon--bot ${baseClassName}__icon--active`}
            color="transparent"
          />
        );
        break;
      case LABS.OBSERVATORY.key:
        currentComponent = (
          <ObservatorySpaceIcon
            className={`${baseClassName}__icon ${baseClassName}__icon--observatory ${baseClassName}__icon--active`}
            color="transparent"
          />
        );
        break;
      case LABS.LIVECHAT.key:
        currentComponent = (
          <LiveChatSpaceIcon
            className={`${baseClassName}__icon ${baseClassName}__icon--livechat ${baseClassName}__icon--active`}
            color="transparent"
          />
        );
        break;
      default:
        currentComponent = null;
        break;
    }

    return currentComponent;
  };

  return (
    <div
      className={classNames(baseClassName, `${baseClassName}--${currentLab}`, {
        [`${baseClassName}--collapsed`]: !isSidebarOpen
      })}
    >
      <LabSwitcherMask
        className={`${baseClassName}__icon-mask`}
      />

      {
        isSidebarOpen ? (
          <div className={`${baseClassName}__icon-container`}>
            {renderIcons()}
          </div>
        ) : null
      }

      <LabSwitcherArrowIcon
        className={`${baseClassName}__arrow`}
        onClick={() => onArrowClick()}
      />

      <div className={`${baseClassName}__label`}>
        {!isSidebarOpen ? renderActiveIcon() : null}

        {
          isSidebarOpen ? (
            <Fragment>
              {
                currentLab === LABS.ORG.key ? (
                  <Typography.Text className="fw-800">{t('Workspace')}</Typography.Text>
                ) : currentLab === LABS.BOT.key ? (
                  <Typography.Text><strong className="fw-800">{t('Designer')}</strong></Typography.Text>
                ) : currentLab === LABS.LIVECHAT.key ? (
                  <Typography.Text><strong className="fw-800">{t('LiveChat')}</strong></Typography.Text>
                ) : currentLab === LABS.OBSERVATORY.key ? (
                  <Typography.Text><strong className="fw-800">{t('Observatory')}</strong></Typography.Text>
                ) : null
              }
            </Fragment>
          ) : null
        }
      </div>
    </div>
  );
};

LabSwitcher.propTypes = {
  onArrowClick: PropTypes.func.isRequired,
  currentLab: PropTypes.string.isRequired,
  setCurrentLab: PropTypes.func.isRequired
};

export default LabSwitcher;
